import {flattenTrackingPayload, sanitizeHapiRequest, sanitizeSentryTrackingEvent, sanitizeTrackingPayload} from '../utils/sentryTrackingSanitizer';

let eventProcessorClient = null;
const IGNORED_HAPI_LABEL_NAMES = [
	'error_limitqty',
	'error_newsletter_gdpr_accept_should_be_checked',
	'error_coupon_invalid_code',
	'error_login',
	'error_not_paid',
];

function normalizeOperation(operation) {
	return String(operation || '')
		.trim()
		.slice(0, 120);
}

function getSafeStatus(error, result) {
	const status = error?.status || error?.statusCode || result?.statusCode;
	return Number.isFinite(Number(status)) ? Number(status) : null;
}

function getFailureType(error, result) {
	const status = getSafeStatus(error, result);
	const name = String(error?.name || '').toLowerCase();
	const message = String(error?.message || '').toLowerCase();

	if (name.includes('abort') || name.includes('timeout') || message.includes('timeout') || message.includes('aborted')) return 'timeout';
	if (!status && error) return 'network';
	if (status >= 500) return 'server';
	if (status >= 400) return 'client';
	return 'unexpected';
}

function getSafeBackendCodes(error, result) {
	const data = result?.data || error?.data?.data || error?.data || {};
	const labelsName = data.labels_name;
	const codes = [data.label_name];

	if (typeof labelsName === 'string') codes.push(labelsName);
	else if (Array.isArray(labelsName)) codes.push(...labelsName);
	else if (labelsName && typeof labelsName === 'object') codes.push(...Object.values(labelsName));

	return codes.filter(code => typeof code === 'string').map(code => code.slice(0, 100));
}

function getSafeBackendCode(error, result) {
	return getSafeBackendCodes(error, result)[0] || null;
}

function shouldReportHapiFailure(error, result, ignoredBackendCodes = []) {
	const status = getSafeStatus(error, result);
	const backendCodes = getSafeBackendCodes(error, result);
	if (backendCodes.some(code => ignoredBackendCodes.includes(code))) return false;
	return Boolean(error || (status && status !== 200));
}

function getCheckoutSelectionContext(tracking, error, result) {
	if (tracking?.operation !== 'POST /hapi/latest/webshop/order/') return null;

	const requestBody = tracking?.request?.body;
	const requestData = requestBody?.data ?? requestBody;
	const parcels = Array.isArray(requestData?.parcels)
		? requestData.parcels.map((parcel, index) => ({
				index,
				number: parcel?.number,
				shipping_selected: parcel?.shipping?.selected ?? null,
				pickup_location_selected: parcel?.shipping?.pickup_location?.selected ?? null,
			}))
		: [];
	const selection = {
		request: {
			payment_selected: requestData?.cart?.payments?.selected ?? null,
			parcels,
		},
		response: error?.data ?? result ?? null,
	};

	// Deliberately flatten only checkout selection data before the generic request
	// reaches its field limit. These public shipping/payment/location values remain
	// readable; customer and cart-item data stay outside this context.
	return flattenTrackingPayload(selection, 500);
}

export function useSentryTracking() {
	const nuxtApp = useNuxtApp();
	const config = useAppConfig();

	function getSanitizerOptions() {
		return {
			maskKeys: config.sentryTracking?.maskKeys,
		};
	}

	function getSentrySdk() {
		if (!import.meta.client || !__SENTRY_TRACKING__) return null;
		const sdk = globalThis.__NUXT_BASE_SENTRY__;
		const client = sdk?.getClient?.();
		if (!client) return null;
		if (eventProcessorClient !== client && sdk.addEventProcessor) {
			sdk.addEventProcessor(event => sanitizeSentryTrackingEvent(event, getSanitizerOptions()));
			eventProcessorClient = client;
		}
		return sdk;
	}

	function getHapiFailureDetails(tracking, error, result, duration) {
		const status = getSafeStatus(error, result);
		const failureType = getFailureType(error, result);
		const backendCode = getSafeBackendCode(error, result);
		const route = nuxtApp.$router?.currentRoute?.value;
		const context = {
			operation: tracking.operation,
			method: tracking.method,
			duration_ms: duration,
			status,
			failure_type: failureType,
			backend_code: backendCode,
			route: route?.path,
			template: nuxtApp.$appGlobalData?.template,
			...tracking.context,
		};
		const sanitizerOptions = getSanitizerOptions();
		const request = tracking.request ? sanitizeHapiRequest(tracking.request, sanitizerOptions) : null;
		const response = error?.data ?? result;
		const sanitizedResponse = response !== undefined ? sanitizeTrackingPayload(response, sanitizerOptions) : null;
		const sanitizedError = error
			? sanitizeTrackingPayload(
					{
						error_name: error.name,
						error_message: error.message,
						status: error.status,
						statusCode: error.statusCode,
					},
					sanitizerOptions,
				)
			: null;
		const requestContext = request ? flattenTrackingPayload(request) : null;
		const responseContext = sanitizedResponse ? flattenTrackingPayload(sanitizedResponse) : null;
		const errorContext = sanitizedError ? flattenTrackingPayload(sanitizedError) : null;
		const checkoutSelectionContext = getCheckoutSelectionContext(tracking, error, result);

		return {status, failureType, backendCode, context, request, response: sanitizedResponse, error: sanitizedError, requestContext, responseContext, errorContext, checkoutSelectionContext};
	}

	function addHapiFailureBreadcrumb(sdk, tracking, error, result, duration) {
		const details = getHapiFailureDetails(tracking, error, result, duration);
		sdk.addBreadcrumb?.({
			category: 'hapi.request',
			type: 'default',
			level: 'error',
			message: `HAPI log: ${tracking.operation}`,
			data: {
				...details.context,
				request: details.request,
				response: details.response,
				error: details.error,
			},
		});
		return details;
	}

	async function reportHapiFailure(sdk, tracking, error, result, duration, details = getHapiFailureDetails(tracking, error, result, duration)) {
		return sdk.withScope(scope => {
			scope.setTag('feature', 'hapi');
			scope.setTag('hapi.operation', tracking.operation);
			scope.setTag('failure.type', details.failureType);
			if (details.status) scope.setTag('http.status_code', String(details.status));
			scope.setFingerprint(['hapi-failure', tracking.operation, details.failureType, details.status ? `${Math.floor(details.status / 100)}xx` : details.backendCode || 'none']);
			scope.setContext('tracking', details.context);
			if (details.requestContext) scope.setContext('hapi_request', details.requestContext);
			if (details.responseContext) scope.setContext('hapi_response', details.responseContext);
			if (details.errorContext) scope.setContext('hapi_error', details.errorContext);
			if (details.checkoutSelectionContext) scope.setContext('checkout_selection', details.checkoutSelectionContext);
			return sdk.captureException(new Error(`HAPI failure: ${tracking.operation}`));
		});
	}

	function captureErrorPage(name, data = {}) {
		const sdk = getSentrySdk();
		if (!sdk) return null;
		const route = nuxtApp.$router?.currentRoute?.value;
		const reason = String(data.reason || '').trim().slice(0, 100);
		const display = String(data.display || '').trim().slice(0, 100);
		const hasStatusCode = Object.prototype.hasOwnProperty.call(data, 'status_code');
		const rawStatusCode = data.status_code;
		const statusCode = rawStatusCode === null || rawStatusCode === undefined || rawStatusCode === '' ? Number.NaN : Number(rawStatusCode);
		const statusCodeTag = Number.isFinite(statusCode) ? String(statusCode) : 'unknown';
		const context = flattenTrackingPayload(
			sanitizeTrackingPayload(
				{
					name,
					route: route?.path,
					template: nuxtApp.$appGlobalData?.template,
					...data,
				},
				getSanitizerOptions(),
			),
		);

		return sdk.withScope(scope => {
			scope.setTag('feature', 'error-page');
			scope.setTag('error.page', name);
			if (hasStatusCode) scope.setTag('error.status_code', statusCodeTag);
			if (reason) scope.setTag('error.reason', reason);
			if (display) scope.setTag('error.display', display);
			scope.setFingerprint(reason ? ['error-page', name, reason] : ['error-page', name]);
			scope.setContext('error_page', context);
			return sdk.captureMessage(`Error page displayed: ${name}`, 'error');
		});
	}

	async function trackHapiRequest(tracking, runner) {
		const operation = normalizeOperation(tracking?.operation);
		if (!operation) return runner({setTerminalError: () => {}});
		const normalizedTracking = {...tracking, operation};
		const sdk = getSentrySdk();
		if (!sdk) return runner({setTerminalError: () => {}});

		return sdk.startSpan(
			{
				name: normalizedTracking.operation,
				op: 'hapi.request',
				attributes: {
					'hapi.operation': normalizedTracking.operation,
					'http.request.method': normalizedTracking.method,
				},
			},
			async span => {
				const startedAt = performance.now();
				let terminalError = null;

				try {
					const result = await runner({setTerminalError: error => (terminalError = error)});
					const duration = Math.round(performance.now() - startedAt);
					const status = getSafeStatus(terminalError, result);
					const responseFailed = terminalError || (status && status !== 200);
					const shouldReport = shouldReportHapiFailure(terminalError, result, IGNORED_HAPI_LABEL_NAMES);

					span?.setAttribute?.('hapi.duration_ms', duration);
					if (status) span?.setAttribute?.('http.response.status_code', status);
					span?.setAttribute?.('hapi.success', !responseFailed);

					if (shouldReport) {
						const details = addHapiFailureBreadcrumb(sdk, normalizedTracking, terminalError, result, duration);
						await reportHapiFailure(sdk, normalizedTracking, terminalError, result, duration, details);
					}
					return result;
				} catch (error) {
					const duration = Math.round(performance.now() - startedAt);
					span?.setAttribute?.('hapi.duration_ms', duration);
					span?.setAttribute?.('hapi.success', false);
					const details = addHapiFailureBreadcrumb(sdk, normalizedTracking, error, error?.data, duration);
					await reportHapiFailure(sdk, normalizedTracking, error, error?.data, duration, details);
					throw error;
				}
			},
		);
	}

	return {
		captureErrorPage,
		trackHapiRequest,
	};
}
